import { useEffect, useState } from 'react';

import {
  faBarsFilter,
  faCheck,
  faClose,
  faTrash,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faBookmark } from '@fortawesome/free-regular-svg-icons';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@repo/videa-components/src/index';
import { cn } from '@repo/videa-components/src/lib/utils';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@repo/videa-components/src/ui/dialog';
import { Input } from '@repo/videa-components/src/ui/input';
import { Label } from '@repo/videa-components/src/ui/label';
import { isValid, subWeeks } from 'date-fns';
import { useAtom } from 'jotai';
import { useHistory, useLocation } from 'react-router-dom';

import {
  useCreateInsightsSavedView,
  useDeleteInsightsSavedView,
  useGetInsightsSavedViews,
  useUpdateInsightsSavedView,
} from '@/react-query/insights/useInsightsSavedViews';

import {
  DEFAULT_MATERIALITY_FILTER_COUNTS,
  DEFAULT_MATERIALITY_FILTER_DOLLARS,
  DEFAULT_MATERIALITY_FILTER_OPPORTUNITY_COUNTS,
  DEFAULT_MATERIALITY_FILTER_OPPORTUNITY_DOLLARS,
  endDateAtom,
  isCountsAtom,
  materialityFilterOpportunityCountsAtom,
  materialityFilterOpportunityDollarsAtom,
  materialityFilterProductionCountsAtom,
  materialityFilterProductionDollarsAtom,
  selectedPracticeGroupsAtom,
  selectedPracticesAtom,
  selectedProviderRoleAtom,
  startDateAtom,
  taskTypeAtom,
  txTypeAtom,
} from '../../atoms';

export const SavedViews = () => {
  const [name, setName] = useState<string>('');

  const [selectedPractices, setSelectedPractices] = useAtom(
    selectedPracticesAtom,
  );
  const [selectedRegions, setSelectedRegions] = useAtom(
    selectedPracticeGroupsAtom,
  );
  const [selectedProviderRole, setSelectedProviderRole] = useAtom(
    selectedProviderRoleAtom,
  );
  const [selectedTaskType, setSelectedTaskType] = useAtom(taskTypeAtom);
  const [selectedTxType, setSelectedTxType] = useAtom(txTypeAtom);
  const [
    selectedMaterialityFilterProductionDollars,
    setSelectedMaterialityFilterProductionDollars,
  ] = useAtom(materialityFilterProductionDollarsAtom);
  const [
    selectedMaterialityFilterProductionCounts,
    setSelectedMaterialityFilterProductionCounts,
  ] = useAtom(materialityFilterProductionCountsAtom);
  const [
    selectedMaterialityFilterOpportunityDollars,
    setSelectedMaterialityFilterOpportunityDollars,
  ] = useAtom(materialityFilterOpportunityDollarsAtom);
  const [
    selectedMaterialityFilterOpportunityCounts,
    setSelectedMaterialityFilterOpportunityCounts,
  ] = useAtom(materialityFilterOpportunityCountsAtom);
  const [isCounts, setIsCounts] = useAtom(isCountsAtom);
  const [startDate, setStartDate] = useAtom(startDateAtom);
  const [endDate, setEndDate] = useAtom(endDateAtom);

  const { data: savedViews } = useGetInsightsSavedViews();

  const { mutate: deleteSavedView } = useDeleteInsightsSavedView();

  const { mutate: createSavedView } = useCreateInsightsSavedView();

  const { mutate: updateSavedView } = useUpdateInsightsSavedView();

  const location = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(location.search);
  const selectedDashboard = location.pathname.includes('hygiene')
    ? 'hygiene'
    : 'clinical';
  const providerDrilldown = location.pathname.includes('provider/')
    ? location.pathname.split('provider/')[1]?.split('/')[0]
    : undefined;
  const practiceDrilldown = location.pathname.includes('practice/')
    ? location.pathname.split('practice/')[1]?.split('/')[0]
    : undefined;
  const regionDrilldown = location.pathname.includes('region/')
    ? location.pathname.split('region/')[1]?.split('/')[0]
    : undefined;

  const selectedSavedView = params.get('view');
  const savedView = savedViews?.data.find((v) => v.id === selectedSavedView);

  const viewConfig = {
    practices: selectedPractices,
    regions: selectedRegions,
    providerRole: selectedProviderRole,
    taskType: selectedTaskType,
    txType: selectedTxType,
    materialityFilterProductionDollars:
      selectedMaterialityFilterProductionDollars,
    materialityFilterProductionCounts:
      selectedMaterialityFilterProductionCounts,
    materialityFilterOpportunityDollars:
      selectedMaterialityFilterOpportunityDollars,
    materialityFilterOpportunityCounts:
      selectedMaterialityFilterOpportunityCounts,
    isCounts: isCounts,
    startDate: startDate,
    endDate: endDate,
    selectedDashboard: selectedDashboard,
    providerDrilldown: providerDrilldown,
    practiceDrilldown: practiceDrilldown,
    regionDrilldown: regionDrilldown,
  };

  const handleClearView = () => {
    setSelectedPractices([]);
    setSelectedRegions([]);
    setSelectedProviderRole([]);
    setSelectedTaskType([]);
    setSelectedTxType([]);

    setSelectedMaterialityFilterProductionDollars(
      DEFAULT_MATERIALITY_FILTER_DOLLARS,
    );
    setSelectedMaterialityFilterProductionCounts(
      DEFAULT_MATERIALITY_FILTER_COUNTS,
    );

    setSelectedMaterialityFilterOpportunityDollars(
      DEFAULT_MATERIALITY_FILTER_OPPORTUNITY_DOLLARS,
    );
    setSelectedMaterialityFilterOpportunityCounts(
      DEFAULT_MATERIALITY_FILTER_OPPORTUNITY_COUNTS,
    );

    setIsCounts(false);
    setStartDate(new Date(subWeeks(new Date(), 8)));
    setEndDate(new Date());
    setName('');
  };

  const handleCreateSavedView = () => {
    if (selectedSavedView) {
      updateSavedView({
        id: selectedSavedView,
        name,
        view: viewConfig,
      });
    } else {
      createSavedView({
        name,
        view: viewConfig,
      });
    }
  };

  const handleSelectSavedView = (viewId: string) => {
    const view = savedViews?.data.find((v) => v.id === viewId);
    if (view && view.id !== selectedSavedView) {
      const path =
        location.pathname.split('insights')[0] +
        'insights/' +
        (view.view?.selectedDashboard ?? 'clinical') +
        (view?.view?.regionDrilldown ||
        view?.view?.practiceDrilldown ||
        view?.view?.providerDrilldown
          ? `/region/${view?.view?.regionDrilldown}`
          : '') +
        (view?.view?.practiceDrilldown || view?.view?.providerDrilldown
          ? `/practice/${view?.view?.practiceDrilldown}`
          : '') +
        (view?.view?.providerDrilldown
          ? `/provider/${view?.view?.providerDrilldown}`
          : '');

      params.set('view', view.id);
      history.push({ pathname: path, search: params.toString() });
      setName(view.name);
    } else if (view && view.id === selectedSavedView) {
      handleClearView();
      params.delete('view');
      history.push({ pathname: location.pathname, search: params.toString() });
    }
  };

  useEffect(() => {
    if (savedView) {
      setSelectedPractices(savedView?.view?.practices ?? []);
      setSelectedRegions(savedView?.view?.regions ?? []);
      setSelectedProviderRole(savedView?.view?.providerRole ?? []);
      setSelectedTaskType(savedView?.view?.taskType ?? []);
      setSelectedTxType(savedView?.view?.txType ?? []);

      setSelectedMaterialityFilterProductionDollars(
        savedView?.view?.materialityFilterProductionDollars ??
          DEFAULT_MATERIALITY_FILTER_DOLLARS,
      );
      setSelectedMaterialityFilterProductionCounts(
        savedView?.view?.materialityFilterProductionCounts ??
          DEFAULT_MATERIALITY_FILTER_COUNTS,
      );

      setSelectedMaterialityFilterOpportunityDollars(
        savedView?.view?.materialityFilterOpportunityDollars ??
          DEFAULT_MATERIALITY_FILTER_OPPORTUNITY_DOLLARS,
      );
      setSelectedMaterialityFilterOpportunityCounts(
        savedView?.view?.materialityFilterOpportunityCounts ??
          DEFAULT_MATERIALITY_FILTER_OPPORTUNITY_COUNTS,
      );

      setIsCounts(!!savedView?.view?.isCounts);
      setStartDate(
        savedView?.view?.startDate &&
          isValid(new Date(savedView?.view?.startDate))
          ? new Date(savedView?.view?.startDate)
          : new Date(subWeeks(new Date(), 8)),
      );
      setEndDate(
        savedView?.view?.endDate && isValid(new Date(savedView?.view?.endDate))
          ? new Date(savedView?.view?.endDate)
          : new Date(),
      );
      setName(savedView?.name ?? '');
    }
  }, [
    savedView,
    setSelectedPractices,
    setSelectedRegions,
    setSelectedProviderRole,
    setSelectedTaskType,
    setSelectedTxType,
    setSelectedMaterialityFilterProductionDollars,
    setSelectedMaterialityFilterProductionCounts,
    setSelectedMaterialityFilterOpportunityDollars,
    setSelectedMaterialityFilterOpportunityCounts,
    setIsCounts,
    setStartDate,
    setEndDate,
    setName,
  ]);

  return (
    <div className="vai-flex vai-flex-row vai-gap-2">
      {selectedSavedView && (
        <Button
          variant="ghost"
          onClick={() => {
            handleClearView();
            params.delete('view');
            history.push({
              pathname: location.pathname,
              search: params.toString(),
            });
          }}
        >
          <FontAwesomeIcon icon={faClose} />
          <span className="vai-text-sm vai-font-normal">Clear</span>
        </Button>
      )}
      <Dialog>
        <DialogTrigger asChild>
          <Button variant="outline">
            <FontAwesomeIcon icon={faBookmark} />
            <span className="vai-text-sm vai-font-normal">Save</span>
          </Button>
        </DialogTrigger>
        <DialogContent className="vai-max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedSavedView ? 'Update Custom View' : 'Create Custom View'}
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="vai-flex vai-flex-col vai-gap-2">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="focus-visible:vai-ring-0"
            />
          </DialogDescription>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <DialogClose asChild>
              <Button
                variant="default"
                disabled={!name.length}
                onClick={handleCreateSavedView}
              >
                Save
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="vai-justify-between vai-text-left vai-font-normal"
          >
            <FontAwesomeIcon icon={faBarsFilter} />
            <span className="vai-text-sm vai-font-normal">
              {selectedSavedView
                ? savedViews?.data.find((view) => view.id === selectedSavedView)
                    ?.name
                : 'Custom Views'}
            </span>
            <FontAwesomeIcon
              className="vai-text-[#71717A] vai-flex-shrink-0"
              icon={faAngleDown}
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="vai-max-h-[65vh] vai-overflow-y-auto vai-w-96"
          zIndex={1}
        >
          {savedViews?.data.length ? (
            savedViews?.data?.map((view) => (
              <DropdownMenuItem
                key={view.id}
                onClick={(e) => {
                  e.preventDefault();
                }}
              >
                <div className="vai-flex vai-gap-4 vai-items-center vai-justify-between vai-w-full">
                  <button
                    className="vai-flex vai-gap-4 vai-items-center"
                    onClick={() => handleSelectSavedView(view.id)}
                  >
                    <FontAwesomeIcon
                      icon={faCheck}
                      className={cn(
                        selectedSavedView !== view.id && 'vai-opacity-0',
                      )}
                    />

                    <div className="vai-flex vai-flex-col vai-items-start">
                      <span className="vai-max-w-[17rem] vai-text-sm vai-text-left vai-font-normal vai-text-ellipsis vai-overflow-hidden vai-whitespace-nowrap">
                        {view.name}
                      </span>
                      <span className="vai-max-w-[17rem] vai-text-[12px] vai-font-normal vai-text-left vai-text-muted-foreground vai-text-ellipsis vai-overflow-hidden vai-whitespace-nowrap">
                        Updated By: {view.updatedBy} •{' '}
                        {new Date(view.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </button>

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <FontAwesomeIcon
                          icon={faTrash}
                          className="vai-text-muted-foreground"
                        />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="vai-max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Delete Custom View?</DialogTitle>
                      </DialogHeader>
                      <DialogDescription>
                        This custom view will be permanently deleted and will no
                        longer be available to any users in your organization.
                        Are you sure you want to remove it?
                      </DialogDescription>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline">Go Back</Button>
                        </DialogClose>
                        <DialogClose asChild>
                          <Button
                            variant="destructive"
                            onClick={() => {
                              deleteSavedView({ id: view.id });
                              if (view.id === selectedSavedView) {
                                params.delete('view');

                                history.push({
                                  pathname: location.pathname,
                                  search: params.toString(),
                                });
                              }
                            }}
                          >
                            Remove View
                          </Button>
                        </DialogClose>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </DropdownMenuItem>
            ))
          ) : (
            <DropdownMenuItem className="vai-text-muted-foreground vai-flex vai-justify-center">
              No custom views
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
